import { useState } from "react";
import { Mail, Phone, MapPin, User, Edit, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

import { useVehicleCard } from "@/hooks/useVehicleCard";
import { OwnerSearchDialog } from "@/components/dialogs/OwnerSearchDialog";
import { UserSummary } from "@/types/userDetails";
import { getLanguage } from "@/utils/languageUtils";
import {
  vehicleCardUILabels,
  vehicleCardToastLabels,
  commonMiscLabels,
} from "@/i18n";

interface EditableContactInfoProps {
  vehicleCardId: string;
  customerName: string;
  email: string;
  phone: string;
  postalCode: string;
  labelOverrides?: {
    postalCode?: string;
  };
}

export const EditableContactInfo = ({
  vehicleCardId,
  customerName,
  email,
  phone,
  postalCode,
  labelOverrides = {},
}: EditableContactInfoProps) => {

  const { updateOwnerAsync, isUpdatingOwner } = useVehicleCard();

  const [showOwnerSearch, setShowOwnerSearch] = useState(false);

  const handleSelectOwner = (owner: UserSummary) => {
    setShowOwnerSearch(false);

    // Automatically save the selected owner
    handleSaveOwner(owner._id);
  };

  const handleSaveOwner = async (customerId: string) => {
    try {
      await updateOwnerAsync({ vehicleCardId, customerId });

      const toastLabels = vehicleCardToastLabels(getLanguage());
      toast.success(toastLabels.ownerInfoUpdated ||
          vehicleCardUILabels(getLanguage()).ownerInfoUpdated, { description: toastLabels.ownerUpdated });
    } catch (error) {
      const toastLabels = vehicleCardToastLabels(getLanguage());
      toast.error(vehicleCardUILabels(getLanguage()).updateError, { description: toastLabels.ownerUpdateError });
    }
  };

  // Check if owner information is missing
  const hasOwnerInfo = customerName && (email || phone);
  const uiLabels = vehicleCardUILabels(getLanguage());
  const commonLabels = commonMiscLabels(getLanguage());

  return (
    <Card className="shadow-md rounded-lg">
      <CardHeader className="p-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-lg">
            <User className="h-5 w-5" />
            {uiLabels.ownerInfo}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowOwnerSearch(true)}
            disabled={isUpdatingOwner}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        {!hasOwnerInfo && (
          <Alert variant="destructive" className="mb-3">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{uiLabels.ownerInfoMissing}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-3 text-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.name}</span>
            <span className="font-medium">{customerName}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.email}</span>
            <a
              href={`mailto:${email}`}
              className="text-blue-500 hover:underline flex items-center gap-1"
            >
              <Mail className="h-3 w-3" />
              <span className="break-all">{email}</span>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">{commonLabels.phone}</span>
            <a
              href={`tel:${phone}`}
              className="text-blue-500 hover:underline flex items-center gap-1"
            >
              <Phone className="h-3 w-3" />
              {phone}
            </a>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground">
              {labelOverrides.postalCode || uiLabels.location}
            </span>
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {postalCode}
            </div>
          </div>
        </div>
      </CardContent>

      <OwnerSearchDialog
        open={showOwnerSearch}
        onOpenChange={setShowOwnerSearch}
        onSelectOwner={handleSelectOwner}
      />
    </Card>
  );
};
